import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useContent } from "../../lib/useFetch";
import { Switch } from "../ui/switch";

const KEY = "aa_gdpr_consent";
const readConsent = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch (e) { return null; } };
const enabled = (doc) => doc && (doc.fields?.show_popup || "").toLowerCase() !== "no";
const versionOf = (doc) => doc?.updated_at || "v1";

export const hasConsent = (category) => !!readConsent()?.[category];
export const openCookieSettings = () => window.dispatchEvent(new Event("aa-gdpr-open"));

const useConsentTick = () => {
  const [, tick] = useState(0);
  useEffect(() => { const f = () => tick((x) => x + 1); window.addEventListener("aa-gdpr", f); return () => window.removeEventListener("aa-gdpr", f); }, []);
};

export const useGdprPending = () => {
  const c = useContent();
  useConsentTick();
  if (!c.gdpr) return !c.site;
  return enabled(c.gdpr) && readConsent()?.version !== versionOf(c.gdpr);
};

const Category = ({ c, name, value, onChange, locked }) => {
  const { trc } = useLang();
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl bg-white/5 p-4" data-testid={`gdpr-cat-${name}`}>
      <div><p className="text-sm font-semibold">{trc(c, "gdpr", `${name}_label`)}</p><p className="mt-0.5 text-xs leading-relaxed text-white/60">{trc(c, "gdpr", `${name}_text`)}</p></div>
      <Switch checked={value} disabled={locked} onCheckedChange={onChange} data-testid={`gdpr-toggle-${name}`}
        className="shrink-0 data-[state=checked]:bg-[#00A8B5] data-[state=unchecked]:bg-white/20 disabled:opacity-100" />
    </div>
  );
};

export const GdprPopup = () => {
  const { trc } = useLang();
  const c = useContent();
  const doc = c.gdpr;
  const [saved, setSaved] = useState(readConsent);
  const [forced, setForced] = useState(false);
  const [custom, setCustom] = useState(false);
  const [sel, setSel] = useState({ analytics: !!saved?.analytics, marketing: !!saved?.marketing });
  useEffect(() => { const f = () => { const s = readConsent(); setSel({ analytics: !!s?.analytics, marketing: !!s?.marketing }); setForced(true); setCustom(true); }; window.addEventListener("aa-gdpr-open", f); return () => window.removeEventListener("aa-gdpr-open", f); }, []);
  if (!enabled(doc) || (!forced && saved?.version === versionOf(doc))) return null;
  const save = (choice) => {
    const v = { necessary: true, ...choice, version: versionOf(doc), at: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(v));
    setSaved(v);
    setSel({ analytics: !!v.analytics, marketing: !!v.marketing });
    setForced(false);
    setCustom(false);
    window.dispatchEvent(new Event("aa-gdpr"));
  };
  const ghost = "rounded-full border border-white/30 px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-white/10";
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 p-4 md:p-6" data-testid="gdpr-popup">
      <div className="mx-auto max-h-[90vh] max-w-4xl overflow-y-auto rounded-3xl border border-white/10 bg-[#0A2540]/95 p-6 text-white shadow-2xl backdrop-blur-xl md:p-7">
        <div className="flex flex-col gap-5 md:flex-row md:items-center">
          <ShieldCheck size={34} className="shrink-0 text-[#00A8B5]" />
          <div className="flex-1">
            <p className="font-display text-lg font-bold" data-testid="gdpr-title">{trc(c, "gdpr", "title")}</p>
            <p className="mt-1 text-sm leading-relaxed text-white/75" data-testid="gdpr-text">{trc(c, "gdpr", "text")}{" "}
              <Link to="/privacy" className="font-semibold text-[#FF8A65] underline" data-testid="gdpr-policy-link">{trc(c, "gdpr", "policy_link_label")}</Link></p>
          </div>
        </div>
        {custom && (
          <div className="mt-5 grid gap-3 md:grid-cols-3" data-testid="gdpr-categories">
            <Category c={c} name="necessary" value locked />
            <Category c={c} name="analytics" value={sel.analytics} onChange={(v) => setSel({ ...sel, analytics: v })} />
            <Category c={c} name="marketing" value={sel.marketing} onChange={(v) => setSel({ ...sel, marketing: v })} />
          </div>
        )}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          {custom
            ? <button onClick={() => save(sel)} className={ghost} data-testid="gdpr-save">{trc(c, "gdpr", "save_label")}</button>
            : <button onClick={() => setCustom(true)} className={ghost} data-testid="gdpr-customize">{trc(c, "gdpr", "customize_label")}</button>}
          <button onClick={() => save({ analytics: false, marketing: false })} className={ghost} data-testid="gdpr-reject">{trc(c, "gdpr", "reject_label")}</button>
          <button onClick={() => save({ analytics: true, marketing: true })} className="btn-buoy !py-2.5" data-testid="gdpr-accept">{trc(c, "gdpr", "accept_label")}</button>
        </div>
      </div>
    </div>
  );
};
